import type { ReactNode } from 'react';
import { AIOS_WALKTHROUGH_URL, EXTERNAL_LINK_PROPS } from '../../lib/links';
import { trackEvent } from '../../lib/analytics';

/**
 * One call to action everywhere on /auraos: "Book your 15-minute
 * walkthrough". Every button goes to the same Cal.com page, tagged with where
 * it was clicked, so we can see which section books calls.
 *
 * `metadata[source]` rides in the link's query string. The `notes` prefill
 * (used by the Diagnose list) was tested on Cal.com and lands in the booking
 * form's "Additional notes" field. The metadata tag shows up on a real
 * booking, so check the first one that comes in.
 */

export const CTA_LABEL = 'Book your 15-minute walkthrough';

export type CtaSource =
  | 'chat'
  | 'home-nav'
  | 'home-hero'
  | 'home-dashboard'
  | 'home-services'
  | 'home-aios'
  | 'home-process'
  | 'home-contact'
  | 'hero'
  | 'nav'
  | 'mobile'
  | 'diagnose'
  | 'staff'
  | 'proof'
  | 'scene'
  | 'next'
  | 'pricing'
  | 'closing';

export const walkthroughUrl = (source: CtaSource, notes?: string): string => {
  const p = new URLSearchParams();
  if (notes) p.set('notes', notes);
  p.set('metadata[source]', source);
  return `${AIOS_WALKTHROUGH_URL}?${p.toString()}`;
};

export const CtaLink = ({
  source,
  notes,
  className,
  children,
  analytics,
}: {
  source: CtaSource;
  notes?: string;
  className?: string;
  children?: ReactNode;
  analytics?: Record<string, string | number | boolean>;
}) => (
  <a
    className={className}
    href={walkthroughUrl(source, notes)}
    {...EXTERNAL_LINK_PROPS}
    onClick={() => trackEvent('walkthrough_cta_click', { source, ...analytics })}
  >
    {children ?? CTA_LABEL}
  </a>
);
